import { useMemo } from "react";
import { bool, string, number, object, shape, arrayOf, oneOf } from "prop-types";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { CommentTypes } from "@repo/utils";
import { db } from "@client";
import { registerCommenterQuery } from "@query";
import { LOCKED_FEATURES, TEAM_ROLES } from "@utils";
import { useEditorWebsocket, useFeatureGuardian } from "@hooks";
import { useEstimateEditorSettings, useEstimateEditorStore, useEstimateComments } from "@tools";
import { ChatMessageEditor } from "@components";
import { ChatThreadContents } from "./ChatThreadContents";
import useStyle from "./ChatThread.style";

export const ChatThread = observer(({
  group={},
  showNewMessageEditor=true,
  ...toPass
}) => {
  const { isProposal, allowCommenting } = useEstimateEditorSettings();
  const { projectHash } = useParams();
  const socket = useEditorWebsocket();
  const commentStore = useEstimateComments();
  const editorStore = useEstimateEditorStore();
  const classes = useStyle();
  const { t } = useTranslation();

  const { checkAccess, FeatureAlert } = useFeatureGuardian(
    LOCKED_FEATURES.COMMENTS
  );

  const { commenterData } = commentStore;
  const { name, path, viewedType, thread, version } = group;
  
  const projectTeamMembers = useMemo(() => (
    viewedType === CommentTypes.INTERNAL
      ? editorStore?.teamMembers
      : editorStore?.teamMembers?.filter(member => member.teamRoleId !== TEAM_ROLES.TECHNICAL)
  ), [viewedType, editorStore?.teamMembers]);
  
  const handleCommentAdd = async (newMessage, login, mentions) => {
    if(!isProposal) {
      const allowed = await checkAccess();
      if ( !allowed ) return;
    }
    
    let cData = commenterData ? { ...commenterData } : undefined;

    if (!cData?.fullName && projectHash && login) {
      if(!cData) {
        const d = await registerCommenterQuery(projectHash, "");
        cData = {
          uuid: d.uuid,
          fullName: d.fullname || d.fullName,
          color: d.color,
        }
        db.comment.put({
          ...cData,
          project: projectHash,
          timestamp: new Date().getTime()
        });
      } else {
        // @todo: update user name in db
        cData.fullName = login;
        commentStore.commenterData?.setName(login)
        db.comment.where("uuid").equals(cData.uuid).modify({ fullName: cData.fullName });
      }
    }
    
    const data = await socket?.requestCommentAdd(
      cData,
      path,
      name,
      newMessage,
      mentions,
      viewedType === CommentTypes.INTERNAL,
      thread?.uuid
    );
    
    if(thread)
      thread.addReply(data);
    else
      group.addThread(viewedType, data);
    
    if(!group.isCreated)
      commentStore.addGroup(group);
  };

  return (
    <>
      {
        Boolean(thread) &&
        <ChatThreadContents
          thread={thread}
          version={version}
          viewedType={viewedType}
          path={path}
          {...toPass}
        />
      }
      {
        allowCommenting && showNewMessageEditor
        ? <div className={classes.editorContainer}>
            <ChatMessageEditor
              onCommit={handleCommentAdd}
              placeholder={t("views.editor.comments.placeholder")}
              requireAuth={!commenterData?.fullName}
              projectTeamMembers={projectTeamMembers}
            />
          </div>
          : (
            !thread &&
            <span className="ml-2 my-2 transparent-2">
              {t(`views.editor.comments.${viewedType === CommentTypes.INTERNAL ? "no_internal" : "no_external"}`)}
            </span>
          )
      }
      <FeatureAlert />
    </>
  );
});

const CommentType = shape({
  uuid: string.isRequired,
  body: string.isRequired,
  createdAt: string.isRequired,
  replies: arrayOf(object),
  user: shape({
    uuid: string.isRequired,
    fullName: string.isRequired,
    color: string,
  }),
  path: string,
})

ChatThread.propTypes = {
  group: shape({
    path: string.isRequired,
    viewedType: oneOf([CommentTypes.INTERNAL, CommentTypes.EXTERNAL]).isRequired,
    name: string,
    internal: CommentType,
    external: CommentType,
  }),
  maxDisplayReplies: number,
  useMaxHeight: bool,
  showNewMessageEditor: bool,
};
